// API Configuration with environment detection
// This file handles dynamic API URL selection based on environment and backend availability

// Environment-based URLs
const DEVELOPMENT_API_URL = 'http://localhost:8000';
const PRODUCTION_API_URL = 'https://api.rapidcmc.com';

// Function to check if local backend is available
const checkLocalBackend = async () => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 2000); // 2 second timeout
    
    const response = await fetch(`${DEVELOPMENT_API_URL}/api/health`, {
      method: 'GET',
      signal: controller.signal,
      mode: 'cors'
    });
    
    clearTimeout(timeoutId);
    return response.ok;
  } catch (error) {
    console.log('Local backend not available, using production API');
    return false;
  }
};

// Function to get the appropriate API URL
export const getApiUrl = async () => {
  // Force production mode if environment variable is set
  if (process.env.REACT_APP_FORCE_PRODUCTION === 'true') {
    console.log('Forcing production API:', PRODUCTION_API_URL);
    return PRODUCTION_API_URL;
  }
  
  // In production build, always use production URL
  if (process.env.NODE_ENV === 'production') {
    return PRODUCTION_API_URL;
  }
  
  // In development, check if local backend is running
  if (process.env.NODE_ENV === 'development') {
    const localAvailable = await checkLocalBackend();
    if (localAvailable) {
      console.log('Using local development API:', DEVELOPMENT_API_URL);
      return DEVELOPMENT_API_URL;
    } else {
      console.log('Local backend not available, using production API:', PRODUCTION_API_URL);
      return PRODUCTION_API_URL;
    }
  }
  
  // Fallback to environment variable or production
  return process.env.REACT_APP_API_URL || PRODUCTION_API_URL;
};

// Cached API URL to avoid multiple checks
let cachedApiUrl = null;
let cacheTime = 0;
const CACHE_DURATION = 30000; // 30 seconds

export const getCachedApiUrl = async () => {
  const now = Date.now();
  
  // Return cached URL if it's still valid
  if (cachedApiUrl && (now - cacheTime) < CACHE_DURATION) {
    return cachedApiUrl;
  }
  
  // Get fresh URL and cache it
  cachedApiUrl = await getApiUrl();
  cacheTime = now;
  
  return cachedApiUrl;
};

// Export the URLs for direct use if needed
export { DEVELOPMENT_API_URL, PRODUCTION_API_URL };
